import React, { useEffect, useRef } from "react";
import * as topojson from "topojson-client";
import * as d3 from "d3";
import { legend } from "../functions/utils";

export default function MapComponent({
  dataMapa,
  tipoMapa,
  dataFiltered,
  topologia,
  topologiaGS,
  baseTopo,
  dataProyectada,
  regionSelect,
  setAnnoSelect,
  annoSelect,
  handleAnno,
  handlePlay,
}) {
  const svgRef = useRef(null);

  const filterComunasBy = (filter) => {
    const reducer = (key) => {
      return function reducer(accumulator, { properties }) {
        const { canonical_key } = properties;
        const dist = properties[key];
        if (accumulator[dist]) {
          accumulator[dist].push(canonical_key);
        } else {
          accumulator[dist] = [canonical_key];
        }
        return accumulator;
      };
    };
    let comunas = baseTopo.objects.base.geometries.reduce(reducer(filter), {});

    if (filter === "Region") {
      comunas["Gran Santiago"] = topologiaGS.objects.base.geometries.reduce(
        reducer(filter),
        {}
      ).undefined;
    }
    return comunas;
  };

  const getMapConf = (mapType) => {
    const geoObj = getGeoObj(mapType);
    const mapProjection = projection(geoObj);
    return {
      geoObj: geoObj,
      mapProjection: mapProjection,
    };
  };

  const getGeoObj = (mapType) => {
    const topo = topos[mapType];

    return {
      type: "GeometryCollection",
      geometries: topo.geometries.map((comuna) => {
        const mesh = topo.extra
          ? topojson.mesh(topologiaGS, comuna)
          : topojson.mesh(topologia, comuna);
        return {
          ...mesh,
          properties: {
            canonical_key: comuna.properties.canonical_key,
            centroid: d3.geoCentroid(mesh),
            comuna: comuna.properties.comuna,
          },
        };
      }),
    };
  };

  const filterTopologia = (filter, distList) => {
    const geometries = topologia.objects.base.geometries;
    if (filter === "Gran Santiago") {
      return {
        type: "GeometryCollection",
        geometries: topologiaGS.objects.base.geometries,
        extra: true,
      };
    }

    if (filter === "País") {
      return {
        type: "GeometryCollection",
        geometries: geometries.filter((geom) =>
          distList.includes(geom.properties.canonical_key)
        ),
      };
    }

    return {
      type: "GeometryCollection",
      geometries: geometries.filter((geom) =>
        distList[filter].includes(geom.properties.canonical_key)
      ),
    };
  };

  const dataBounds = (data, dataMapa) => {
    if (dataMapa === "Total nuevos predios") {
      return [
        d3.format(".2r")(
          Math.min.apply(
            Math,
            data.map(function (o) {
              return o.total_predios;
            })
          )
        ),
        d3.format(".2r")(
          Math.max.apply(
            Math,
            data.map(function (o) {
              return o.total_predios;
            })
          )
        ),
      ];
    }

    else {
      return [
        d3.format(".2r")(
          Math.min.apply(
            Math,
            data.map(function (o) {
              return o.nuevos_predios_cada_mil;
            })
          )
        ),
        d3.format(".2r")(
          Math.max.apply(
            Math,
            data.map(function (o) {
              return o.nuevos_predios_cada_mil;
            })
          )
        ),
      ];
    }
    }

    const color = (
      data,
      dataMapa,
      val = false,
      tickThresholds = undefined
    ) => {
      let bounds = tickThresholds === undefined ? dataBounds(data, dataMapa) : [tickThresholds[0], tickThresholds[tickThresholds.length - 1]]
      
      if (dataMapa === 'Total nuevos predios') {
        let scale = d3.scaleQuantize(bounds, [
          "#FF9B00",  
          "#69c5ff", 
          "#008ee9",
          "#005f9d",
          "#00406a",
          "#00121e"
        ])
        if (val) {
          return scale(val)
        } 
        return scale
      }
      else {
          let scale = d3.scaleQuantize(bounds, [
           "#FF9B00", 
          "#69c5ff", 
          "#008ee9",
          "#005f9d",
          "#00406a",
          "#00121e"
        ])
        if (val) {
          return scale(val)
        } 
        return scale
      }
    } ;

    const map_color = (comuna, data, dataScale, tickThresholds, dataMapa) => {
      
      let val = data.find(el => el.comuna_key === comuna);
      
      // Si la comuna no tiene fila para el año seleccionado, se pinta como sin dato.
      
      // Sin esto, `val` queda undefined y el mapa completo deja de renderizar.
      
      if (!val) return "#EDEDED";
      
      if (dataMapa === "Total nuevos predios") {
        if (val.total_predios === 0) {
        
          return "#e9d6e8";
        }
        return Object.is(val.total_predios, null)
          ? "#818ae9"
          : color(dataScale, dataMapa, val.total_predios, [-500, 0, 500, 1000, 1500, 2000, 2500]);
      }
      else { 
        if (val.nuevos_predios_cada_mil === 0) {
        
          return "#35b1ff";
        }
        return Object.is(val.nuevos_predios_cada_mil, null)
          ? "#EDEDED"
          : color(dataScale, dataMapa, val.nuevos_predios_cada_mil, [-50, 0, 50, 100, 150, 200, 250]);
      }
    };

  const projection = (geom) => {
    return d3.geoMercator().fitSize([600, 600], geom);
  };

  const comunasPorRegion = filterComunasBy("Region");
  const comunasPorPais = filterComunasBy("País");
  const comunasPorProvincia = filterComunasBy("Provincia");

  const provincias = Object.keys(comunasPorProvincia).sort();

  const topos = {
    Regional: filterTopologia(regionSelect, comunasPorRegion),
    Provincial: filterTopologia(provincias[0], comunasPorProvincia),
    País: filterTopologia("País", comunasPorPais.undefined),
  };

  const createTooltip = (d, data, dataMapa) => {
    let tooltipClass = "tooltip-content";

    const fallData = data.find(
      el => el.comuna_key === d.target.__data__.properties.canonical_key);

    // Si la comuna no tiene fila para el año seleccionado, se muestra S/I con el
    // nombre que trae la geometría. Sin esto, el tooltip revienta con undefined.
    if (!fallData) {
      const nombre = d.target.__data__.properties.Comuna || "";
      return `
        <div class=${tooltipClass}>
          <strong>${nombre.toUpperCase()}</strong>
          <i class="texto-secundario">S/I</i>
        </div>`;
    }
      
    if (dataMapa === "Total nuevos predios") { 
      if (fallData.total_predios === null) {
      return `
      <div class=${tooltipClass}>
            <strong>
              
              ${fallData.comuna.toUpperCase()}
            </strong>
        <i class="texto-secundario">
              S/I
        </i>
            
      </div>
  `;
    }
    return `
    <div class=${tooltipClass}>
    
    <span class="texto-primario">
      ${fallData.Nombre_Comuna}
    </span>

    <span class="texto-secundario">
    ${fallData.total_predios}
    </span>

      </div>
    </div>
  `;}

    else { 
      if (dataMapa.nuevos_predios_cada_mil === null) {
      return `
      <div class=${tooltipClass}>
            <strong>
              
              ${fallData.comuna.toUpperCase()}
            </strong>
        <i class="texto-secundario">
              S/I
        </i>
            
      </div>
  `;
    }
    return `
    <div class=${tooltipClass}>
    
    <span class="texto-primario">
      ${fallData.Nombre_Comuna}
    </span>

    <span class="texto-secundario">
      ${fallData.nuevos_predios_cada_mil.toLocaleString('es-ES', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}
    </span>

      </div>
    </div>
  `;}
  };

  useEffect(() => {
    const svgEl = d3.select(svgRef.current);
    svgEl.selectAll("*").remove(); // Clear svg content before adding new elements
    const svg = svgEl
      .attr("width", "100%")
      .attr("height", "80%")
      .style("width", "100%")
      .style("height", "100%")
      .attr("viewBox", function () {
        return tipoMapa === "País" ? "0 200 600 250" : "-300 0 1200 750";
      });

    let { geoObj, mapProjection } = getMapConf(tipoMapa);

    // Tooltip
    let div = d3.select(".arrow-box");

    //   Legend
    if (dataFiltered && dataFiltered.length) {
      svg
        .append("g")
        .attr("transform", function () {
          return tipoMapa === "País"
            ? `translate(200, 390)`
            : `translate(100, 650) `;
        })
        .append(() =>
          legend({
            color: color(dataProyectada, dataMapa),
            title: dataMapa === "Total nuevos predios" ? "Nuevos predios habitacionales" : "Nuevos predios habitacionales cada 1000 hogares",
            marginTop: tipoMapa === "País" ? 15 : 15,
            subtitle: "Fuente: Elaboración propia basado en datos INE-CEPAL",
            subtitleFontSize: tipoMapa === "País" ? "8px" : "23px",
            subtitleFontWeight: "200",
            subTitlemarginTop: tipoMapa === "País" ? 18 : 13,
            subTitlemarginBottom: tipoMapa === "País" ? 18 : 100,
            tickFormat: ".0f",
            width: tipoMapa === "País" ? 200 : 500,
            height: tipoMapa === "País" ? 42 : 68,
            titleFontSize: tipoMapa === "País" ? "10px" : "30px",
            titleFontWeight: "200",
            tickFontSize: tipoMapa === "País" ? "8px" : "25px",
            tickSize: tipoMapa === "País" ? 2 : 20,
            tickWidth: tipoMapa === "País" ? 0.8 : 1,
            tickPos: tipoMapa === "País" ? 1 : 7,
            tickThresholds: dataMapa === "Total nuevos predios" ? [-1849, 0, 500, 1000, 1500, 2000, 19686] : [-480, 0, 50, 100, 150, 200, 5891],
            roundedValues: false,
            sin_info: false,
          })
        );
    }

    // Mapa
    svg
      .append("g")
      .selectAll("path")
      .data(geoObj.geometries)
      .enter()
      .append("path")
      .attr("class", function () {
        return tipoMapa === "País" ? "decideChilePais" : "decideChileRegion";
      })
      .attr("fill", (d, i) =>
        map_color(
          d.properties.canonical_key,
          dataFiltered,
          dataProyectada,
          [0, 20, 40, 60, 80, 100],
          dataMapa
        )
      )
      .attr("d", d3.geoPath(mapProjection))
      .attr("transform", function () {
        return tipoMapa === "País" ? "rotate(-90 300 300)" : null;
      })

      

      .on("mouseover", function (d, i) {
        svg.selectAll("path").attr("fill-opacity", 0.2);
        d3.select(this).transition().duration("25").attr("fill-opacity", 1);
  
        //Tooltip
        div.html(
          createTooltip(
            d,
            dataFiltered,
            dataMapa
          ))
      
        div
            .transition()
            .duration("25")
            .style("display", "block")
            .style("opacity", 1);
            div
            .style("left", (d.pageX < window.innerWidth / 2 ? d.pageX + 20 : d.pageX - 220) + "px")
            .style("top", d.pageY + 20 + "px");
          })
  
        .on("mouseout", function (d) {
          svg.selectAll("path").attr("fill-opacity", 1);
          d3.select(this).transition().duration("25");
          //Tooltip
          div.transition().duration("25").style("opacity", 0);
        });
      
      });

  return <svg ref={svgRef} />;
}